import React from 'react'

import { Skeleton as MuiSkeleton, Stack } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

type SkeletonVariant = 'text' | 'rounded' | 'rectangular' | 'circular'

interface SkeletonProps {
  variant?: SkeletonVariant
  width?: number | string
  height?: number | string
  // When > 1, renders that many stacked lines (the last one shortened) — for
  // multi-line text placeholders (paragraphs, descriptions).
  lines?: number
  animation?: 'pulse' | 'wave' | false
  sx?: SxProps<Theme>
}

/**
 * Design-system skeleton placeholder shown while content loads.
 *
 * Defaults to a rounded bar with a wave shimmer; theme-aware (MUI derives the
 * base tint from the palette, so it reads on light and dark). Compose these to
 * match the SHAPE of the content being loaded — a circular avatar, a title bar,
 * a few text lines — rather than a generic spinner. See the working-conventions
 * skeleton rule (§19) for when this is required.
 */
export const Skeleton = ({
  variant = 'rounded',
  width,
  height,
  lines = 1,
  animation = 'wave',
  sx,
}: SkeletonProps) => {
  if (lines > 1) {
    return (
      <Stack spacing={1} sx={sx}>
        {Array.from({ length: lines }).map((_, i) => (
          <MuiSkeleton
            key={i}
            variant="rounded"
            animation={animation}
            width={i === lines - 1 ? '60%' : (width ?? '100%')}
            height={height ?? 14}
            sx={{ borderRadius: 1 }}
          />
        ))}
      </Stack>
    )
  }

  return (
    <MuiSkeleton
      variant={variant}
      animation={animation}
      width={width}
      height={height}
      sx={{ borderRadius: variant === 'circular' ? '50%' : 1, ...sx }}
    />
  )
}

Skeleton.displayName = 'Skeleton'
